<template>
  <div>
    <div class="login-header">
      <h2>{{ t('loginTitle') }}</h2>
      <button class="close-btn" @click="$emit('close')" :aria-label="t('close')">&times;</button>
    </div>

    <div class="form">

      <input
        v-model="username"
        type="email"
        :placeholder="t('email')"
        autocomplete="username"
        :readonly="lockEmail"
        :class="{ 'locked': lockEmail }"
        @keyup.enter="submit"
      />

      <div class="password-field">
        <input
          :value="password"
          :type="showPassword ? 'text' : 'password'"
          :placeholder="t('password')"
          autocomplete="current-password"
          @input="onPasswordInput"
          @keyup.enter="submit"
        />
        <button
          v-if="passwordTouched && password.length > 0"
          type="button"
          class="reveal-btn"
          :aria-label="showPassword ? t('passwordHide') : t('passwordShow')"
          :title="showPassword ? t('passwordHide') : t('passwordShow')"
          @click="showPassword = !showPassword"
        >{{ showPassword ? '🙈' : '👁' }}</button>
      </div>

      <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
      <div v-if="infoMessage" class="success-message">{{ infoMessage }}</div>

      <button :disabled="!canSubmit || busy" @click="submit">
        {{ t('login') }}
      </button>

      <div class="link-row">
        <a href="#" class="forgot-link" @click.prevent="$emit('forgot')">{{ t('forgotPassword') }}</a>
        <a href="#" class="forgot-link" @click.prevent="$emit('register')">{{ t('registerCompany') }}</a>
      </div>

    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { useI18n } from '../i18n/index.js'

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

function validEmail(email) {
  return !!email && EMAIL_REGEX.test(email)
}

export default defineComponent({
  name: 'LoginView',

  props: {
    initialEmail: { type: String, default: '' },
    lockEmail: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    infoMessage: { type: String, default: '' }
  },

  emits: ['submit', 'forgot', 'register', 'close'],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      username: this.initialEmail,
      password: '',
      showPassword: false,
      // Auge-Button erst zeigen, wenn manuell getippt wurde (siehe
      // onPasswordInput). Autofill/vorausgefuellt setzt das nicht -> Button
      // bleibt weg. Verhalten wie in view/login.jsp gewuenscht.
      passwordTouched: false
    }
  },

  computed: {
    canSubmit() {
      return validEmail(this.username) && this.password.length > 0
    }
  },

  methods: {
    onPasswordInput(e) {
      this.password = e.target.value
      // Auge-Button nur bei echtem Tippen/Editieren zeigen. Native Autofill
      // liefert inputType null, Passwort-Manager oft 'insertReplacementText' /
      // 'insertFromPaste' -> beide setzen passwordTouched NICHT. Nur manuelle
      // Eingabe (insertText) oder Loeschen zaehlt.
      var it = e.inputType
      if (it === 'insertText'
          || it === 'deleteContentBackward'
          || it === 'deleteContentForward') {
        this.passwordTouched = true
      }
    },

    submit() {
      if (!this.canSubmit || this.busy) return
      // The orchestrator now runs a discovery-login first; role and
      // account selection happen in dedicated picker views afterwards.
      this.$emit('submit', {
        username: this.username,
        password: this.password
      })
    }
  }
})
</script>

<style scoped>
.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.login-header h2 { margin: 0; font-size: 1.25rem; }

.close-btn {
  border: none;
  background: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
}
.close-btn:hover { color: #000; }

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
input[type="email"],
input[type="password"],
input[type="text"] {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font: inherit;
  font-size: 16px;
}
.password-field {
  position: relative;
  display: flex;
}
.password-field input {
  flex: 1;
  padding-right: 40px;
}
.reveal-btn {
  position: absolute;
  right: 4px;
  top: 0;
  bottom: 0;
  width: 36px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: inherit;
}
.reveal-btn:hover { opacity: 0.7; }
button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font: inherit;
  font-size: 16px;
  cursor: pointer;
}
button:disabled { background: #c7c9d1; cursor: not-allowed; }
input.locked { background: #eef0f4; color: #555; cursor: not-allowed; }

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
.success-message {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}

.link-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.forgot-link {
  font-size: 13px;
  color: #6b7280;
  text-decoration: none;
}
.forgot-link:hover { color: #2563eb; text-decoration: underline; }
</style>
